import { useEffect, useCallback, useMemo, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { verifyChainConfig } from '../../../helpers';
import { BigNumberish, ethers } from 'ethers';
import {
    ITradeManager,
    ITradeManagerOrders,
    ITradeManagerOrders__factory,
    ITradeManager__factory,
} from '../../../config/contracts/typechain';
import useUserPositions from './useUserPositions';
import useUserTrades from './useUserTrades';
import { config } from '../../../config/config';
import { ReadyState } from 'react-use-websocket';
import { useWebSocket } from 'react-use-websocket/dist/lib/use-websocket';
import useUserOrders from './useUserOrders';
import useToastState from '../../../context/toast/useToastState';
import {
    CHAIN_INFO,
    EnabledChains,
    OrderBookActionMapper,
    OrderBookType,
} from '../../../config/constants';
import Button from '../../../components/Button';
import { Indexed } from 'ethers/lib/utils';
import useUserPositionIds from './useUserPositionIds';
import useTransactionState from '../../../context/transaction/useTransactionState';
import useTransactionLog from './useTransactionLog';
import useApproveToken from '../useApproveToken';

const listeners = new Map<string, boolean>();

enum TradeEvents {
    OpenPosition = 'OpenedPositionViaSignature',
    ClosedPosition = 'ClosedPositionViaSignature',
    PartiallyClosedPosition = 'PartiallyClosedPositionViaSignature',
    ExtendedPosition = 'ExtendedPositionViaSignature',
    ExtendedPositionToLeverage = 'ExtendedPositionToLeverageViaSignature',
    AddedMarginToPosition = 'AddedMarginToPositionViaSignature',
    RemovedMarginFromPosition = 'RemovedMarginFromPositionViaSignature',
    PositionLiquidated = 'positionLiquidatedEvent',
}

type EventData = {
    tradePair: string;
    id: BigNumberish;
    signatureHash: Indexed;
    event?: ethers.Event;
    eventName: TradeEvents;
};

const useOrderListener = () => {
    const { chainId } = useWeb3React();
    const [socketProvider, setSockerProvider] =
        useState<ethers.providers.WebSocketProvider>();
    const [eventData, setEventData] = useState<EventData[]>([]);

    const providerUrl = useMemo(() => {
        try {
            if (!chainId || !verifyChainConfig(chainId)) {
                return null;
            }

            return config.endpoints[chainId].wsProvider;
        } catch (error) {
            return null;
        }
    }, [chainId]);

    const websocket = useWebSocket(providerUrl, {
        shouldReconnect: () => true,
        reconnectAttempts: 5,
        reconnectInterval: 1000,
        onClose: () => {
            setSockerProvider(undefined);
            listeners.clear();
        },
    });

    const blockExplorer = useMemo(
        () => CHAIN_INFO[chainId as EnabledChains[number]]?.blockExplorer,
        [chainId]
    );

    const { addUserPositionId, removeUserPositionId } = useUserPositionIds();
    const { userPositions, updateUserPositions } = useUserPositions();
    const { userTrades, updateUserTrades } = useUserTrades();
    const { userOrders, updateUserOrderExecuteStatus } = useUserOrders();
    const { updateTransactionLog } = useTransactionLog();
    const { removePendingTransaction } = useTransactionState();
    const { setToast } = useToastState();

    const { mutate } = useApproveToken(
        config.addresses[chainId as EnabledChains[number]]?.collateral,
        config.addresses[chainId as EnabledChains[number]]?.tradeManager,
        '0'
    );

    const toastMessage = useCallback(
        (orderType: OrderBookType, isMarketOrder: boolean, event: ethers.Event) => {
            const orderTypeLabel = isMarketOrder ? 'Market' : 'Limit';

            setToast(
                {
                    type: 'success',
                    message: (
                        <>
                            {orderType === OrderBookType.OPEN
                                ? `${orderTypeLabel} Order`
                                : `${OrderBookActionMapper[orderType]} ${orderTypeLabel}`}{' '}
                            Filled!&nbsp;
                            <Button
                                variant='transparent'
                                externalLink={`${blockExplorer}/tx/${event.transactionHash}`}
                            >
                                View TX
                            </Button>
                        </>
                    ),
                },
                event.transactionHash
            );
        },
        [blockExplorer, setToast]
    );

    const openedPositionEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.OpenPosition,
                },
            ]);
        },
        []
    );

    const partiallyClosedEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.PartiallyClosedPosition,
                },
            ]);
        },
        []
    );

    const extendedPositionEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.ExtendedPosition,
                },
            ]);
        },
        []
    );

    const extendedPositionToLeverageEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.ExtendedPositionToLeverage,
                },
            ]);
        },
        []
    );

    const addedMarginEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.AddedMarginToPosition,
                },
            ]);
        },
        []
    );

    const removedMarginEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.RemovedMarginFromPosition,
                },
            ]);
        },
        []
    );

    const closedPositionEvent = useCallback(
        async (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => {
            setEventData((prev) => [
                ...prev,
                {
                    event,
                    id,
                    signatureHash,
                    tradePair,
                    eventName: TradeEvents.ClosedPosition,
                },
            ]);
        },
        []
    );

    const positionLiquidatedEvent = useCallback(
        async (tradePair: string, id: BigNumberish, signatureHash: Indexed) => {
            setEventData((prev) => [
                ...prev,
                {
                    tradePair,
                    id,
                    signatureHash,
                    eventName: TradeEvents.PositionLiquidated,
                },
            ]);
        },
        []
    );

    const tradeEventListenersMapper: Record<
        Exclude<TradeEvents, TradeEvents.PositionLiquidated>,
        (
            tradePair: string,
            id: BigNumberish,
            signatureHash: Indexed,
            event: ethers.Event
        ) => void
    > = useMemo(
        () => ({
            [TradeEvents.OpenPosition]: openedPositionEvent,
            [TradeEvents.PartiallyClosedPosition]: partiallyClosedEvent,
            [TradeEvents.AddedMarginToPosition]: addedMarginEvent,
            [TradeEvents.RemovedMarginFromPosition]: removedMarginEvent,
            [TradeEvents.ExtendedPositionToLeverage]: extendedPositionToLeverageEvent,
            [TradeEvents.ExtendedPosition]: extendedPositionEvent,
            [TradeEvents.ClosedPosition]: closedPositionEvent,
        }),
        [
            openedPositionEvent,
            partiallyClosedEvent,
            addedMarginEvent,
            removedMarginEvent,
            extendedPositionToLeverageEvent,
            extendedPositionEvent,
            closedPositionEvent,
        ]
    );

    const attachListeners = useCallback(
        (
            tradeManagerOrdersContract: ITradeManagerOrders,
            tradeManagerContract: ITradeManager,
            socket: ethers.providers.WebSocketProvider
        ) => {
            if (socket.websocket.readyState !== ReadyState.OPEN) return;

            Object.entries(tradeEventListenersMapper).forEach(
                ([eventName, eventListener]) => {
                    const event = `${eventName}_${tradeManagerOrdersContract.address}`;

                    if (!listeners.get(event)) {
                        tradeManagerOrdersContract.on(eventName, eventListener);
                        listeners.set(event, true);
                    }
                }
            );

            tradeManagerContract.on('PositionLiquidated', positionLiquidatedEvent);
        },
        [tradeEventListenersMapper, positionLiquidatedEvent]
    );

    const detachListeners = useCallback(
        (
            tradeManagerOrdersContract: ITradeManagerOrders,
            tradeManagerContract: ITradeManager,
            socket: ethers.providers.WebSocketProvider
        ) => {
            if (socket.websocket.readyState !== ReadyState.OPEN) return;

            Object.entries(tradeEventListenersMapper).forEach(
                ([eventName, eventListener]) => {
                    const event = `${eventName}_${tradeManagerOrdersContract.address}`;

                    if (listeners.get(event)) {
                        tradeManagerOrdersContract.off(eventName, eventListener);
                    }
                }
            );

            listeners.clear();

            tradeManagerContract.off('PositionLiquidated', positionLiquidatedEvent);
        },
        [tradeEventListenersMapper, positionLiquidatedEvent]
    );

    useEffect(() => {
        const socket = websocket.getWebSocket();

        if (!socketProvider && socket && websocket.readyState === ReadyState.OPEN) {
            const webSocketProvider = new ethers.providers.WebSocketProvider(
                socket as any,
                chainId
            );

            // manually trigger wsReady because we're passing our own websocket which is returned by "react-use-websocket"
            // and the onopen function within WebSocketProvider isn't triggered
            webSocketProvider._wsReady = true;

            setSockerProvider(webSocketProvider);
        }
    }, [socketProvider, chainId, websocket]);

    useEffect(() => {
        if (!userPositions || !userOrders || !eventData.length) return;

        eventData.map(
            async ({ eventName, signatureHash, tradePair, event, id }, index) => {
                const order = userOrders.find(
                    ({ sigHash }) => sigHash === signatureHash.hash
                );

                if (!order) return;

                if (eventName === TradeEvents.PositionLiquidated) {
                    removeUserPositionId(tradePair, id.toString());

                    await updateUserOrderExecuteStatus(signatureHash);

                    updateTransactionLog();
                    updateUserPositions(userPositions);
                    updateUserTrades(userTrades);

                    setEventData((prev) =>
                        prev.filter((_, prevIndex) => prevIndex !== index)
                    );

                    return;
                }

                if (!event) return;

                if (eventName === TradeEvents.OpenPosition) {
                    await addUserPositionId(tradePair, id.toString());

                    toastMessage(OrderBookType.OPEN, order.isMarketOrder, event);

                    await updateUserOrderExecuteStatus(signatureHash);
                    // updateTransactionLog();
                    // updateUserPositions(userPositions);
                    removePendingTransaction(signatureHash.hash);
                }

                if (eventName === TradeEvents.PartiallyClosedPosition) {
                    await updateUserOrderExecuteStatus(signatureHash);

                    // updateTransactionLog();
                    await updateUserPositions(userPositions);

                    toastMessage(
                        OrderBookType.PARTIALLY_CLOSE,
                        order.isMarketOrder,
                        event
                    );

                    removePendingTransaction(signatureHash.hash);
                }

                if (eventName === TradeEvents.ExtendedPosition) {
                    await updateUserOrderExecuteStatus(signatureHash);

                    toastMessage(OrderBookType.EXTEND, order.isMarketOrder, event);

                    // updateTransactionLog();
                    updateUserPositions(userPositions);

                    removePendingTransaction(signatureHash.hash);
                }

                if (eventName === TradeEvents.ExtendedPositionToLeverage) {
                    await updateUserOrderExecuteStatus(signatureHash);

                    toastMessage(
                        OrderBookType.EXTEND_LEVERAGE,
                        order.isMarketOrder,
                        event
                    );

                    updateTransactionLog();

                    updateUserPositions(userPositions);

                    removePendingTransaction(signatureHash.hash);
                }

                if (eventName === TradeEvents.AddedMarginToPosition) {
                    await updateUserOrderExecuteStatus(signatureHash);

                    toastMessage(OrderBookType.ADD_MARGIN, order.isMarketOrder, event);

                    updateTransactionLog();

                    updateUserPositions(userPositions);

                    removePendingTransaction(signatureHash.hash);
                }
                if (eventName === TradeEvents.RemovedMarginFromPosition) {
                    await updateUserOrderExecuteStatus(signatureHash);

                    toastMessage(OrderBookType.REMOVE_MARGIN, order.isMarketOrder, event);

                    updateTransactionLog();

                    updateUserPositions(userPositions);

                    removePendingTransaction(signatureHash.hash);
                }

                if (eventName === TradeEvents.ClosedPosition) {
                    await removeUserPositionId(tradePair, id.toString());

                    await updateUserOrderExecuteStatus(signatureHash);

                    toastMessage(OrderBookType.CLOSE, order.isMarketOrder, event);

                    updateTransactionLog();

                    updateUserPositions(userPositions);

                    updateUserTrades(userTrades);

                    removePendingTransaction(signatureHash.hash);
                }

                setEventData((prev) =>
                    prev.filter((_, prevIndex) => prevIndex !== index)
                );
            }
        );

        mutate();
    }, [
        eventData,
        userPositions,
        userOrders,
        toastMessage,
        addUserPositionId,
        updateUserOrderExecuteStatus,
        removePendingTransaction,
        removeUserPositionId,
        updateTransactionLog,
        updateUserPositions,
        updateUserTrades,
        mutate,
        userTrades,
    ]);

    useEffect(() => {
        try {
            if (
                !(chainId && verifyChainConfig(chainId)) ||
                !socketProvider ||
                socketProvider.websocket.readyState !== ReadyState.OPEN
            ) {
                return;
            }
        } catch (error) {
            return;
        }

        const tradeManagerOrdersContract = ITradeManagerOrders__factory.connect(
            config.addresses[chainId].tradeManagerOrders,
            socketProvider
        );

        const tradeManagerContract = ITradeManager__factory.connect(
            config.addresses[chainId].tradeManager,
            socketProvider
        );

        attachListeners(tradeManagerOrdersContract, tradeManagerContract, socketProvider);

        return () => {
            detachListeners(
                tradeManagerOrdersContract,
                tradeManagerContract,
                socketProvider
            );
        };
    }, [socketProvider, chainId, attachListeners, detachListeners]);
};

export default useOrderListener;
